import { ArrowRight } from 'lucide-react';

import { LAUNCH_STEPS, STEPS_SECTION } from '@/data/homeContent';

/**
 * "Launch your store in three simple steps", the left half of the first
 * two-column row.
 *
 * The panel is the tinted surface and the three steps are white cards on it,
 * which is the way round the design has it - a white panel with tinted cards
 * reads as three disabled tiles rather than three stops on a path.
 *
 * Each card is faced with its own illustration, which already carries the
 * step's glyph, so the card draws no icon of its own; only the step number is
 * left, moved to the far corner to keep clear of that badge.
 *
 * The arrow between two steps is decoration on a row that reads left to right,
 * so it only appears once the steps actually sit side by side; stacked on a
 * phone, the order of the list is the sequence.
 */
export function LaunchSteps() {
  return (
    <section
      aria-labelledby="home-steps"
      className="rounded-2xl border border-line/70 bg-surface/70 p-4 shadow-tile sm:p-5"
    >
      <h2 id="home-steps" className="text-lg font-bold tracking-tight text-ink sm:text-xl">
        {STEPS_SECTION.heading}
      </h2>
      <p className="mt-1 text-xs leading-relaxed text-muted sm:text-sm">
        {STEPS_SECTION.subheading}
      </p>

      <ol className="mt-4 grid gap-3 sm:grid-cols-3 sm:gap-5">
        {LAUNCH_STEPS.map((step, index) => (
          <li key={step.step} className="relative flex">
            <div className="relative flex w-full flex-row items-center overflow-hidden sm:flex-col sm:items-stretch rounded-xl border border-line bg-white">
              {/* The artwork is the card's background, and the copy is pulled
                  back up over its foot.

                  It stays in flow rather than being pinned to the card,
                  because that is what makes the card as tall as the drawing
                  wants to be at whatever width it lands on. Pinned, with the
                  card on a fixed height, a third of a half-width panel leaves
                  the drawing floating above a dead band of white.

                  `contain`, not `cover`, and the ratio is the drawings' own:
                  the crop a cover would take is a different slice at every
                  width, and the first thing it eats is the glyph badged into
                  the top-left corner - which is what tells the three cards
                  apart now that they draw no icon of their own. What contain
                  costs instead is a few pixels of white down each side of the
                  one drawing that is a little narrower than the rest, on a
                  card that is already white. */}
              {/* On a phone the drawing is a thumbnail beside the copy: at
                  full width the narrower drawings sat centred in a white band
                  and each card was taller than the screen's width. */}
              <img
                src={step.image}
                alt=""
                className="aspect-[5/6] w-28 shrink-0 object-cover object-top sm:w-full sm:object-contain"
                loading="lazy"
                decoding="async"
              />

              {/* The overlap is what makes the drawing a background rather
                  than a picture with a caption under it; the gradient is what
                  keeps the copy readable across it, since the three drawings
                  each fade out to a different colour. */}
              <div className="relative min-w-0 flex-1 py-3 pe-10 ps-4 sm:-mt-12 sm:bg-linear-to-t sm:from-white sm:from-60% sm:to-transparent sm:px-4 sm:pb-4 sm:pt-8">
                <h3 className="text-sm font-bold leading-snug tracking-tight text-ink">
                  {step.title}
                </h3>
                <p className="mt-1 text-xs sm:hyphens-auto sm:text-justify leading-relaxed text-muted">
                  {step.description}
                </p>
              </div>

              <span className="absolute end-3 top-3 text-lg sm:text-xl font-bold tracking-tight text-muted/50">
                {step.step}
              </span>
            </div>

            {index < LAUNCH_STEPS.length - 1 ? (
              // Centred in the 20px gap between two cards, so it points across
              // the gutter rather than sitting on top of the next card.
              <ArrowRight
                className="absolute -end-[18px] top-1/2 hidden size-4 -translate-y-1/2 text-brand-500 sm:block rtl-flip"
                aria-hidden
              />
            ) : null}
          </li>
        ))}
      </ol>
    </section>
  );
}
